import { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import styled, { useTheme } from "styled-components";
import BookService from "../../services/BookService";
import ReservationService from "../../services/ReservationService";
import { getCurrentUser } from "../../services/AuthService";
import placeholderImage from "../../assets/logo.png";
import Button from "../CommonComponents/Button";
import pdfLogo from "../../assets/pdfRed.png"
import PdfModal from "../BookComponents/PdfModal";
import { useNavigate } from "react-router-dom";
import MessageModal from "../CommonComponents/MessageModal";
import Spinner from "../CommonComponents/Spinner";
import AuthorModal from "../AuthorComponents/AuthorModal";
import { Card, Chip, ModalOverlay, Overlay, Wrapper, ModalCloseButton } from "../CommonComponents/DataCard";
import { PdfLink } from "../CommonComponents/Components";
import { ROLES } from '../../services/Constants';
import EditBook from "./EditBook";
import SimilarBooks from "./SimilarBooks";
import BookReviews from "./BookReviews";


const TopSection = styled.div`
  display: flex;
  gap: 8px;
`;

const Cover = styled.img`
  width: 250px;
  height: 350px;
`;

export const InfoRow = styled.div`
  display: flex;             
  flex-direction: row;       
  justify-content: flex-start; 
  align-items: start;       
  gap: 1rem;           
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ccc;
  padding: 2rem; 
`;





const DESCRIPTION_LIMIT = 200;

function BookDetail({ bookId, user }) {
  const theme = useTheme();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationMessage, setReservationMessage] = useState("");
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [selectedAuthorId, setSelectedAuthorId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);


  const navigate = useNavigate();

  const fetchBook = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await BookService.getBookById(bookId);
      const thumbUrl = await BookService.getBookThumbnail(bookId);
      setBook({
        ...data,
        cover:
          thumbUrl ||
          (data.cover ? `data:image/jpeg;base64,${data.cover}` : placeholderImage),
      });
    } catch (err) {
      setError("Knjiga nije pronađena");
      setBook(null);
    } finally {
      setLoading(false);
    }
  }, [bookId]);
  const canEdit = user &&
    (user.role === ROLES.EMPLOYEE || user.role === ROLES.ADMIN);


  useEffect(() => {
    if (bookId) fetchBook();
  }, [bookId, fetchBook]);



  const handleReservation = async () => {
    try {
      const user = await getCurrentUser();
      if (!user || user.authenticated === false) {
        navigate("/prijava");
        return;
      }
      const response = await ReservationService.addReservation(user.id, bookId);

      setReservationMessage(response.message);
      setReservationSuccess(response.success);
      setShowReservationModal(true);
    } catch (error) {
      if (error.message === "Niste prijavljeni") {
        navigate("/prijava");
      } else {
        console.error("Greška pri rezervaciji:", error);
        setReservationMessage("Došlo je do greške pri rezervaciji");
        setReservationSuccess(false);
        setShowReservationModal(true);
      }
    }
  };

  const handleCloseModal = () => {
    setShowReservationModal(false);
    if (reservationSuccess) fetchBook();
  }

  if (loading) return <Spinner />;
  if (error) return <p>{error}</p>;
  if (!book) return null;


  return (
    <Card>
      <TopSection>
        <Cover src={book.cover} alt={book.name} />
        <InfoGroup
          style={{
            alignItems: "center",
          }}>
          <h2>{book.name}</h2>
          <InfoRow>
            <InfoGroup
              style={{
                marginLeft: "1rem"
              }}>
              <p><strong>Godina izdavanja:</strong> {book.yearOfPublishment ? new Date(book.yearOfPublishment).getFullYear() : "Nepoznato"}</p>
              <p><strong>Mjesto izdavanja:</strong> {book.placeOfPublishment || "Nepoznato"}</p>
              <p><strong>Uvez:</strong> {book.binding || "Nepoznato"}</p>
              <p><strong>Žanr:</strong> {book.genre || "Nepoznato"}</p>
            </InfoGroup>
            <InfoGroup >
              <p><strong>Broj stranica:</strong> {book.numOfPages || "Nepoznato"}</p>
              <p>
                <strong>{book.authors?.length > 1 ? "Autori:" : "Autor:"}</strong>{" "}
                {book.authors?.length > 0 ? (
                  book.authors.map(a => (
                    <span
                      key={a.id}
                      style={{ cursor: "pointer", marginRight: "4px", textDecoration: "underline" }}
                      onClick={() => setSelectedAuthorId(a.id)}
                    >
                      {a.name} {a.surname}
                    </span>
                  ))
                ) : "Nepoznato"}
              </p>
              <p><strong>Izdavač:</strong> {book.publisher?.name || "Nepoznato"}</p>
              <p><strong>{book.illustrators?.length > 1 ? "Ilustratori:" : "Ilustrator:"}</strong> {book.illustrators?.map(i => `${i.name} ${i.surname}`).join(", ") || "Nepoznato"}</p>
            </InfoGroup>
          </InfoRow>
        </InfoGroup>
      </TopSection>

      <BottomSection>
        <InfoRow>
          <InfoGroup
            style={{
              marginRight: "2rem"
            }}>
            <p style={{
              wordWrap: "break-word",
              overflowWrap: "break-word",
              lineHeight: "1.5",
              margin: 0,
              maxWidth: "500px"
            }}>
              <strong>Kratki opis:</strong>{" "}
              {book.shortDescription
                ? (book.shortDescription.length > DESCRIPTION_LIMIT && !showFullDescription
                    ? (
                      <>
                        {book.shortDescription.slice(0, DESCRIPTION_LIMIT)}…{" "}
                        <span
                          onClick={() => setShowFullDescription(true)}
                          style={{ color: theme.colors.primary, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                        >
                          Pročitaj više
                        </span>
                      </>
                    )
                    : (
                      <>
                        {book.shortDescription}
                        {book.shortDescription.length > DESCRIPTION_LIMIT && (
                          <>
                            {" "}
                            <span
                              onClick={() => setShowFullDescription(false)}
                              style={{ color: theme.colors.primary, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              Prikaži manje
                            </span>
                          </>
                        )}
                      </>
                    )
                  )
                : "Nepoznato"}
            </p>
            {book.note && <p><strong>Bilješke:</strong> {book.note}</p>}
            {book.keywords && (
              <p>
                <strong>Ključne riječi:</strong>{" "}
                {book.keywords.split(",").map((kw, i) => (
                  <Chip key={i}>{kw.trim()}</Chip>
                ))}
              </p>
            )}
            <PdfLink onClick={() => setShowPdf(true)}>
              <img src={pdfLogo} alt="PDF" width="24" height="24" />
              <p>Sadržaj</p>
            </PdfLink>
          </InfoGroup>
          <InfoGroup style={{
            maxWidth: "fit-content"
          }}>
            <p>
              <strong>Ukupno primjeraka:</strong>{" "}
              <span style={{ color: "red" }}>{book.totalCopies}</span>
            </p>

            <p><strong>Dostupnih primjeraka:</strong>  <span style={{ color: "green" }}>{book.availableCopies}</span></p>
            <p><strong>Rezerviranih primjeraka:</strong>  <span style={{ color: "orange" }}>{book.reservedCopies}</span></p>


            <Button tekst={"Rezerviraj"} sirina="100%" radius="8px" akcija={handleReservation} />
            <div style={{
              margin: "4px"
            }}></div>
            {canEdit && (
              <Button
                tekst={"Uredi"}
                sirina="100%"
                radius="8px"
                akcija={() => { setEditing(true) }}
              />
            )}
          </InfoGroup>
        </InfoRow>
      </BottomSection>



      <SimilarBooks bookId={bookId} user={user} />
      <BookReviews bookId={bookId} user={user} />

      {showPdf && <PdfModal content={book.content} onClose={() => setShowPdf(false)} />}

      {editing && ReactDOM.createPortal(
        <Overlay onClick={() => setEditing(false)} style={{ alignItems: "flex-start", padding: "2rem 1rem", overflowY: "auto", boxSizing: "border-box", zIndex: 10000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "fit-content" }}>
            <ModalCloseButton onClick={() => setEditing(false)} title="Zatvori" style={{ position: "absolute", top: "0.75rem", right: "0.75rem", zIndex: 1 }}>×</ModalCloseButton>
            <EditBook book={book} onSaved={() => { setEditing(false); fetchBook(); }} onCancel={() => setEditing(false)} />
          </div>
        </Overlay>,
        document.body
      )}



      <MessageModal
        isOpen={showReservationModal}
        onClose={handleCloseModal}
        title="Rezervacija"
        variant={reservationSuccess ? "success" : "error"}
      >
        {reservationMessage}
      </MessageModal>

      {
        selectedAuthorId && (
          <AuthorModal
            isOpen={!!selectedAuthorId}
            onClose={() => setSelectedAuthorId(null)}
            id={selectedAuthorId}
          />
        )
      }
    </Card >

  );
}

export default BookDetail;
